import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash.isequal';
import { presetIdFor } from '@porter/shared-theme';
import {
  BookingSiteDraft,
  BookingSiteSettings,
} from '../../../types/bookingSite';
import { GalleryImage } from '../../../types/gallery';
import { DraftFaq } from '../../../types/faqs';

const storageKey = (businessId: string) => `bookingSiteDraft:${businessId}`;

function toDraft(
  settings: BookingSiteSettings,
  gallery: GalleryImage[],
  faqs: DraftFaq[],
): BookingSiteDraft {
  return {
    backgroundId: settings.backgroundId,
    primaryId: settings.primaryId,
    accentId: settings.accentId,
    fontId: settings.fontId,
    presetId: settings.presetId,
    instagramHandle: settings.instagramHandle,
    tiktokHandle: settings.tiktokHandle,
    facebookUrl: settings.facebookUrl,
    websiteUrl: settings.websiteUrl,
    gallery,
    faqs,
  };
}

/**
 * presetId labels the four ids rather than being chosen alongside them. Stored,
 * it made returning an option to its preset value read as an unpublished
 * change — the ids matched, the cleared label did not.
 */
function withPreset(draft: BookingSiteDraft): BookingSiteDraft {
  return { ...draft, presetId: presetIdFor(draft) };
}

/**
 * A field typed into and emptied again is not a change: the '' an input reports
 * never matches the stored null, leaving the publish footer up over empty
 * fields.
 */
function normalize(draft: BookingSiteDraft): BookingSiteDraft {
  return Object.fromEntries(
    Object.entries(draft).map(([key, value]) => [key, value || null]),
  ) as BookingSiteDraft;
}

function read(businessId?: string): BookingSiteDraft | null {
  if (!businessId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(businessId));
    return raw ? (JSON.parse(raw) as BookingSiteDraft) : null;
  } catch {
    return null;
  }
}

/**
 * Unsaved edits, persisted across refreshes and tabs. Saving is always explicit
 * — nothing here writes to the API.
 */
function useBookingSiteDraft(
  businessId: string | undefined,
  settings: BookingSiteSettings | null | undefined,
  gallery: GalleryImage[] | null | undefined,
  faqs: DraftFaq[] | null | undefined,
) {
  const [stored, setStored] = useState<BookingSiteDraft | null>(() =>
    read(businessId),
  );

  useEffect(() => {
    setStored(read(businessId));
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return undefined;
    const key = storageKey(businessId);
    const onStorage = (event: StorageEvent) => {
      if (event.key !== key) return;
      setStored(read(businessId));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [businessId]);

  const saved = useMemo(
    () =>
      settings && gallery && faqs
        ? withPreset(toDraft(settings, gallery, faqs))
        : null,
    [settings, gallery, faqs],
  );
  const draft = useMemo(() => {
    const current = stored ?? saved;
    if (!current) return null;
    const currentGallery = current.gallery ?? saved?.gallery ?? [];
    const currentFaqs = current.faqs ?? saved?.faqs ?? [];
    return withPreset(
      normalize({ ...current, gallery: currentGallery, faqs: currentFaqs }),
    );
  }, [stored, saved]);
  const isDirty = !!draft && !!saved && !isEqual(draft, saved);

  const setDraft = useCallback(
    (changes: Partial<BookingSiteDraft>) => {
      if (!businessId || !draft) return;
      const next = { ...draft, ...changes };
      setStored(next);
      try {
        window.localStorage.setItem(
          storageKey(businessId),
          JSON.stringify(next),
        );
      } catch {
        // Private browsing or a full quota — the draft still lives in state.
      }
    },
    [businessId, draft],
  );

  const clearDraft = useCallback(() => {
    setStored(null);
    if (!businessId) return;
    try {
      window.localStorage.removeItem(storageKey(businessId));
    } catch {
      // Nothing to recover from — state is already cleared.
    }
  }, [businessId]);

  return { draft, saved, isDirty, setDraft, clearDraft };
}

export default useBookingSiteDraft;
